import React, {memo} from "react";
import {formikPropertyChanged} from "./index";
import {useTranslation} from "react-i18next";
import FormSection from "../../../components/FormSection";
import Grid from "@mui/material/Grid";
import IncidentField from "./IncidentField";
import IncidentSelect from "./IncidentSelect";
import Checkbox from "@mui/material/Checkbox";
import FormSubSectionTitle from "./FormSubSectionTitle";
import GridItemOffset from "./GridItemOffset";
import {FieldWithTooltip} from "./FormTooltip";


const attributes = [
    'alpineRescue', 'incidentCategory', 'incidentSubcategory', 'groupWithProfessionalGuide', 'numberOfPeopleInvolved',
    'notes'
]


const Incident = memo(({formik, incidentRegistry, incidentSettings, inputDisabled, numberOfPeopleCaught}) => {
    const {t} = useTranslation(['incidents', 'enums']);

    const alpineRescueRegistry = incidentRegistry.data?.alpineRescue ?? [];
    const incidentCategoryRegistry = incidentRegistry.data?.incidentCategoryV2 ?? [];
    const incidentSubcategoryRegistry = incidentRegistry.data?.incidentSubcategory ?? [];

    const filteredSubcategoryRegistry = incidentSubcategoryRegistry.filter((subcategory) => (
        incidentSettings.data?.incidentCategoryGroups?.[formik.values.incidentCategory]?.includes(subcategory.code)
    ));

    const handleChangeCategory = async (event) => {
        const selectedCategory = event.target.value;
        await formik.setFieldValue('incidentCategory', selectedCategory);
        formik.setFieldValue('incidentSubcategory', incidentSettings.data?.incidentCategoryGroups?.[selectedCategory]?.[0]);
    }

    return <FormSection title={t('incidentForm.incidentTitle')}
                        error={attributes.some(attr => Boolean(formik.errors[attr]) && formik.touched[attr])}>
        <Grid container rowSpacing={3} columnSpacing={4}>
            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.alpineRescue')}/>
            </Grid>
            <Grid item xs={12} md={4} offset={{ xs: 0, md: 8}}>
                <IncidentSelect formik={formik}
                                name="alpineRescue"
                                registry={alpineRescueRegistry}
                                disabled={inputDisabled}/>
            </Grid>
            <GridItemOffset xs={12} md={4}/>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.incidentCategory')}/>
            </Grid>
            <Grid item xs={12} md={4}>
                <IncidentSelect formik={formik}
                                name="incidentCategory"
                                onChange={handleChangeCategory}
                                registry={incidentCategoryRegistry}
                                disabled={inputDisabled}/>
            </Grid>
            <Grid item xs={12} md={4}>
                <IncidentSelect formik={formik}
                                name="incidentSubcategory"
                                registry={filteredSubcategoryRegistry}
                                disabled={inputDisabled}/>
            </Grid>
            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.groupWithProfessionalGuide')}/>
            </Grid>

            <Grid item xs={12} md={4}>
                <FieldWithTooltip message={t('incidentForm.tooltip.groupWithProfessionalGuide')}>
                    <Checkbox checked={!!formik.values.groupWithProfessionalGuide}
                              color="info"
                              disabled={inputDisabled}
                              onChange={(e) => formik.setFieldValue("groupWithProfessionalGuide", e.target.checked)}/>
                </FieldWithTooltip>
            </Grid>
            <GridItemOffset xs={12} md={4}/>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.numberOfPeople')}/>
            </Grid>
            <Grid item xs={12} md={4}>
                <FieldWithTooltip message={t('incidentForm.tooltip.numberOfPeopleInvolved')}>
                    <IncidentField formik={formik}
                                   name="numberOfPeopleInvolved"
                                   type="number"
                                   disabled={inputDisabled}/>
                </FieldWithTooltip>
            </Grid>
            <Grid item xs={12} md={4}>
                <IncidentField formik={formik}
                               name="numberOfPeopleCaught"
                               value={numberOfPeopleCaught ?? '0'}
                               disabled
                               type="number"/>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.notes')}/>
            </Grid>
            <Grid item xs={12} md={8} offset={{ xs: 0, md: 8}}>
                <IncidentField formik={formik}
                               name="notes"
                               rows={8}
                               multiline
                               InputProps={{
                                   readOnly: inputDisabled,  // we use readonly and not disable because disable make the input no more scrollable,
                               }}/>
            </Grid>
            <GridItemOffset xs={12} md={4}/>
        </Grid>
    </FormSection>

}, (prevProps, nextProps) => (formikPropertyChanged(prevProps, nextProps, attributes) && nextProps.numberOfPeopleCaught === prevProps.numberOfPeopleCaught));


export default Incident;